import { useMemo } from 'react';
import { useEChart } from './useEChart.js';
import { ChartFrame } from './ChartFrame.js';
import { formatChartAmount, formatCountFa } from './formatters.js';
import type { ChartBaseStateProps, ChartSeries } from './types.js';

export interface LineChartProps extends ChartBaseStateProps {
  readonly categories: readonly string[];
  readonly series: readonly ChartSeries[];
  readonly ariaLabel: string;
  readonly height?: number | undefined;
  readonly valueFormatter?: ((value: number) => string) | undefined;
}

/**
 * Change-over-time, one or more named series over the same category axis.
 * A legend renders only for >=2 series (a single line needs no legend —
 * the card title already names it), matching the categorical fixed color
 * order every ChartKit component shares.
 */
export function LineChart({
  categories,
  series,
  ariaLabel,
  height = 260,
  amountUnit,
  valueFormatter: valueFormatterProp,
  isLoading,
  loadingMessage,
  error,
  status,
  errorMessage,
  onRetry,
  retryLabel,
  emptyMessage,
}: LineChartProps) {
  // Stable identity: a fresh default function on every render is a changed
  // `useEChart` input and would repaint (and re-animate) the chart each time.
  const defaultFormatter = useMemo(
    () => (amountUnit ? (v: number) => formatChartAmount(v, amountUnit) : formatCountFa),
    [amountUnit],
  );
  const valueFormatter = valueFormatterProp ?? defaultFormatter;
  const hasError = Boolean(error || (status && status >= 400));
  const isEmpty =
    !isLoading &&
    !hasError &&
    (series.length === 0 || series.every((s) => s.data.every((v) => v === 0)));
  const containerRef = useEChart(
    (theme) => ({
      grid: {
        left: 8,
        right: 12,
        top: series.length > 1 ? 32 : 20,
        bottom: 28,
        containLabel: true,
      },
      legend:
        series.length > 1
          ? {
              top: 0,
              textStyle: { color: theme.text, fontSize: 12 },
              icon: 'roundRect',
              itemWidth: 10,
              itemHeight: 10,
            }
          : undefined,
      xAxis: {
        type: 'category',
        data: [...categories],
        axisLine: { lineStyle: { color: theme.grid } },
        axisTick: { show: false },
        axisLabel: { color: theme.textMuted, fontSize: 11 },
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: theme.grid } },
        axisLabel: { color: theme.textMuted, fontSize: 11, formatter: valueFormatter },
      },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v: number) => valueFormatter(Number(v)),
      },
      series: series.map((s, i) => ({
        type: 'line' as const,
        name: s.name,
        data: [...s.data],
        smooth: false,
        symbol: 'circle',
        symbolSize: 8,
        lineStyle: { width: 2, color: theme.categorical[i % theme.categorical.length] },
        itemStyle: {
          color: theme.categorical[i % theme.categorical.length],
          borderColor: theme.surface,
          borderWidth: 2,
        },
      })),
    }),
    [categories, series, valueFormatter],
  );

  return (
    <ChartFrame
      containerRef={containerRef}
      height={height}
      ariaLabel={ariaLabel}
      isLoading={isLoading}
      loadingMessage={loadingMessage}
      error={error}
      status={status}
      errorMessage={errorMessage}
      onRetry={onRetry}
      retryLabel={retryLabel}
      isEmpty={isEmpty}
      emptyMessage={emptyMessage}
    />
  );
}
